// Notifications (compact list, Kareem 4 Nov): one row per notification, 44 to 52px, grouped by Today, Yesterday and
// Earlier. A row: a small kind icon (the orb only for what the assistant sent), the title in one line with the detail
// cut to fit, the status (Approved, Ignored) and the time on the right, and a small dismiss on hover. Unread rows have
// a dot and bolder text, never a fill. Dismiss all at the top (the filter on screen), with Undo. Rows that carry an AI
// action have Approve and Ignore inline (optimistic, the same one list as Requests). On the right, what you hear
// about and how (app, push, email), and quiet hours; change them in place.
// Keys: J and K move, D dismisses, A approves, I ignores.
import { useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, LayoutGroup, motion } from 'motion/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  NOTIFICATION_CATEGORIES, NOTIFICATION_CATEGORY_LABELS, PLACE_FIRST, type AppNotification, type NotificationCategory, type NotificationKind, type UserPrefs,
} from '@dpw/shared';
import { api } from '../api';
import { useChange, useNotifications, usePrefs, useUpdatePrefs } from '../lib/queries';
import { useDecide } from '../lib/decide';
import { useHotkeys } from '../lib/keys';
import { duration, ease, spring } from '../lib/motion';
import { addDays, arrived, localDate, localDay } from '../lib/format';
import { useToast } from '../ui/toast';
import { ErrorNote } from '../ui/bits';
import { NOT_IGNORABLE, STATUS_WORD } from '../ui/ApproveCard';
import { Count, DrawnCheck, Orb, SkelList } from '../ui/motion';
import { IconBell, IconCheck, IconClock, IconX } from '../ui/icons';
import { usePhone } from '../lib/media';
import { PhoneNotifications } from '../phone/Notifications';

export type Filter = 'all' | 'approval' | 'reminders' | 'mentions' | 'assigned' | 'due' | 'checkins' | 'knowledge';
export const FILTERS: [Filter, string, NotificationKind | null][] = [
  ['all', 'All', null], ['approval', 'Needs approval', 'approval_request'], ['reminders', 'Reminders', 'reminder_due'], ['mentions', 'Mentions', 'mention'],
  ['assigned', 'Assigned', 'assigned'], ['due', 'Due', 'due'], ['checkins', 'Check-ins', 'question'], ['knowledge', 'Research', 'knowledge'],
];
export const when = (iso: string) => arrived(iso);
export const SEE: Partial<Record<string, string>> = { file_minutes: 'Review each action', create_activity: 'See details', update_activity: 'See details' };

/** The list, its filter and Dismiss (optimistic, with Undo): shared by the desktop page and the phone. */
export function useNotificationList() {
  const q = useNotifications();
  const [filter, setFilter] = useState<Filter>('all');
  const qc = useQueryClient();
  const toast = useToast();
  const open = q.data?.pages[0]?.open ?? 0;
  const kind = FILTERS.find(f => f[0] === filter)![2];
  const items = useMemo(() => (q.data?.pages ?? []).flatMap(p => p.items).filter(n => !kind || n.kind === kind), [q.data, kind]);
  const dismiss = (n: AppNotification) => {
    // Optimistic: the row turns quiet at once; Undo brings it back.
    qc.setQueryData(['notifications'], (d: typeof q.data) => d && {
      ...d, pages: d.pages.map((p, i) => ({ ...p, open: i === 0 ? Math.max(0, p.open - 1) : p.open, items: p.items.map(x => (x.id === n.id ? { ...x, dismissedAt: new Date().toISOString() } : x)) })),
    });
    api.notifications.dismiss(n.id).then(
      () => toast('Dismissed.', { label: 'Undo', run: () => { void api.notifications.restore(n.id).then(() => qc.invalidateQueries()); } }),
      (e: unknown) => toast(e instanceof Error ? e.message : 'Could not dismiss.'),
    ).finally(() => { void qc.invalidateQueries({ queryKey: ['notifications'] }); void qc.invalidateQueries({ queryKey: ['summary'] }); });
  };

  // Dismiss all: every open row the filter shows (the server takes the kind); Undo brings exactly those back.
  const dismissAll = () => {
    const shown = items.filter(n => !n.dismissedAt);
    if (!shown.length) return;
    const at = new Date().toISOString();
    qc.setQueryData(['notifications'], (d: typeof q.data) => d && {
      ...d, pages: d.pages.map((p, i) => ({ ...p, open: i === 0 ? Math.max(0, p.open - shown.length) : p.open, items: p.items.map(x => (!x.dismissedAt && (!kind || x.kind === kind) ? { ...x, dismissedAt: at } : x)) })),
    });
    api.notifications.dismissAll(kind ?? undefined).then(
      r => toast(`Dismissed ${r.ids.length}.`, { label: 'Undo', run: () => { void api.notifications.restoreMany(r.ids).then(() => qc.invalidateQueries()); } }),
      (e: unknown) => toast(e instanceof Error ? e.message : 'Could not dismiss.'),
    ).finally(() => { void qc.invalidateQueries({ queryKey: ['notifications'] }); void qc.invalidateQueries({ queryKey: ['summary'] }); });
  };
  const unread = items.filter(n => !n.dismissedAt).length;

  return { q, filter, setFilter, items, open, dismiss, dismissAll, unread };
}

export type DayGroup = 'Today' | 'Yesterday' | 'Earlier';
/** Newest first, in Today, Yesterday and Earlier (by when each arrived, in the team's time zone). */
export function byDay<T extends { createdAt: string }>(items: T[], today = localDate()): { day: DayGroup; items: T[] }[] {
  const yesterday = addDays(today, -1);
  const sorted = [...items].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const dayOf = (n: T): DayGroup => { const d = localDay(n.createdAt); return d >= today ? 'Today' : d === yesterday ? 'Yesterday' : 'Earlier'; };
  return (['Today', 'Yesterday', 'Earlier'] as const).map(day => ({ day, items: sorted.filter(n => dayOf(n) === day) })).filter(g => g.items.length);
}

export function NotificationsPage() {
  return usePhone() ? <PhoneNotifications /> : <DesktopNotifications />;
}

function DesktopNotifications() {
  const { q, filter, setFilter, items, open, dismiss, dismissAll, unread } = useNotificationList();
  const [focus, setFocus] = useState(0);
  const decide = useDecide();
  const groups = useMemo(() => byDay(items), [items]);
  // The keys move in the order on screen.
  const ordered = useMemo(() => groups.flatMap(g => g.items), [groups]);
  const at = ordered[Math.min(focus, ordered.length - 1)];
  const moveTo = (i: number) => {
    const n = Math.max(0, Math.min(ordered.length - 1, i));
    setFocus(n);
    document.querySelector<HTMLElement>(`[data-note="${ordered[n]?.id}"]`)?.focus();
  };
  useHotkeys({
    j: () => moveTo(focus + 1), k: () => moveTo(focus - 1), ArrowDown: () => moveTo(focus + 1), ArrowUp: () => moveTo(focus - 1),
    d: () => { if (at && !at.dismissedAt) dismiss(at); },
    a: () => { if (at?.action?.status === 'waiting' && !NOT_IGNORABLE.has(at.action.kind) && !PLACE_FIRST.has(at.action.kind)) decide.approve({ ...at.action, title: at.title }); },
    i: () => { if (at?.action?.status === 'waiting' && !NOT_IGNORABLE.has(at.action.kind)) decide.ignore({ ...at.action, title: at.title }); },
  });

  return (
    <>
      <div className="hdr">
        <div className="htl"><h1>Notifications</h1><span className="xs mut"><Count value={open} /> unread</span></div>
        <div className="hact">
          <button type="button" className="bt sm" onClick={dismissAll} disabled={!unread}>Dismiss all</button>
          <a className="bt ghost sm" href="#hear">Preferences</a>
        </div>
      </div>
      <LayoutGroup id="nf">
        <div className="seg2 nfilt" role="tablist" aria-label="Show">
          {FILTERS.map(([k, l]) => (
            <button key={k} role="tab" aria-selected={filter === k} className={filter === k ? 'on' : ''} onClick={() => { setFilter(k); setFocus(0); }}>
              {filter === k && <motion.span layoutId="nf-pill" className="segpill" transition={spring.snappy} />}<span className="segtx">{l}</span>
            </button>
          ))}
        </div>
      </LayoutGroup>
      <div className="nfcols">
        <div className="panel nflist">
          {q.isLoading ? <SkelList rows={6} variant="note" /> : (
            // Keyed by the filter: a new filter shows its list at once; within a filter, dismissing animates in place.
            <LayoutGroup key={filter}>
              {groups.map(g => (
                <section key={g.day} className="nfday" aria-label={g.day}>
                  <h2 className="nfdh">{g.day}</h2>
                  <AnimatePresence initial={false} mode="popLayout">
                    {g.items.map(n => (
                      <motion.div key={n.id} layout="position" transition={spring.gentle} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: duration.fast, ease } }}>
                        <Row n={n} decide={decide} onDismiss={() => dismiss(n)} onFocus={() => setFocus(ordered.indexOf(n))} />
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </section>
              ))}
              {!items.length && <div className="empty0"><b>Nothing here.</b><span className="xs mut">{filter === 'all' ? 'You are all caught up.' : 'Nothing of this kind yet.'}</span></div>}
            </LayoutGroup>
          )}
          {q.hasNextPage && (
            <div className="rowc" style={{ padding: 10 }}>
              <button className="bt sm" onClick={() => void q.fetchNextPage()} disabled={q.isFetchingNextPage}>{q.isFetchingNextPage ? <span className="spin" /> : null}Show older</button>
            </div>
          )}
          <div className="keys xs mut" aria-hidden="true"><kbd>J</kbd><kbd>K</kbd> move <kbd>D</kbd> dismiss <kbd>A</kbd> approve <kbd>I</kbd> ignore</div>
        </div>
        <HearAbout />
      </div>
    </>
  );
}

/**
 * What went wrong, in the service's own words, folded away under "Details". The server sends it to admins only
 * (AppNotification.detail); everyone reads the plain words above it.
 */
export function NoteDetail({ detail }: { detail: string | null }) {
  if (!detail) return null;
  return <details className="ndet"><summary>Details</summary><pre>{detail}</pre></details>;
}

/** The small icon for a row: the orb only for what the assistant sent; a clock for reminders; the person's initials; a bell. */
function KindIcon({ n }: { n: AppNotification }) {
  if (n.isAi) return <span className="nk ai"><Orb size={10} breathe={false} /></span>;
  if (n.kind === 'reminder_due' || n.kind === 'due') return <span className="nk"><IconClock /></span>;
  if (n.actor) return <span className="nk who" title={n.actor.name}>{n.actor.initials}</span>;
  return <span className="nk"><IconBell /></span>;
}

/** One notification, one line. The page passes its one decide (a row does not set up its own), and only a row that
 * asks you something has the answer box (with its own state), so a long list stays cheap to render. */
function Row({ n, decide, onDismiss, onFocus }: { n: AppNotification; decide: ReturnType<typeof useDecide>; onDismiss: () => void; onFocus: () => void }) {
  const on = !n.dismissedAt;
  // The server marks rows that ask this person something; results and other notes have no Answer box.
  const canAnswer = n.payload.answerable === true && !!n.teamCheckId;
  const ctx = typeof n.payload.context === 'string' && n.payload.context ? n.payload.context : null;
  const act = n.action;
  const busy = act ? decide.busy(act.id) : false;
  const via = n.via.filter(v => v !== 'app');
  const href = n.link ?? (act ? `/requests?id=${act.id}` : null);
  const detail = [n.body, ctx].filter(Boolean).join(' · ');
  const [answering, setAnswering] = useState(false);
  return (
    <div className={`nt2 ${on ? 'on' : 'off'}${n.isAi ? ' ai' : ''}`} data-note={n.id} data-kind={act?.kind} tabIndex={-1} onFocus={onFocus}>
      <div className="nl1">
        <span className={`ud${on ? ' on' : ''}`} aria-hidden="true" />{on && <span className="sr">Unread: </span>}
        <KindIcon n={n} />
        <div className="nx" title={[n.title, detail].filter(Boolean).join('. ')}>
          {href ? <Link to={href} className="nlink nti">{n.title}</Link> : <span className="nti">{n.title}</span>}
          {detail && <span className="nds">{detail}</span>}
        </div>
        <div className="nr">
          {act && (act.status === 'waiting' ? (
            NOT_IGNORABLE.has(act.kind)
              // Settling a team check needs a choice between the answers, which the card in Requests shows.
              ? <Link className="bt sm xs2" to={`/requests?id=${act.id}`}>{act.kind === 'confirm_answer' ? 'Choose the right answer' : 'Review the answer'}</Link>
              // An email the assistant could not place: picking its space and track happens in Requests.
              : PLACE_FIRST.has(act.kind) ? <>
                <Link className="bt sm xs2" to={`/requests?id=${act.id}`}>Log it</Link>
                <button className="bt sm xs2" onClick={() => decide.ignore({ ...act, title: n.title })} disabled={busy} aria-label={`Dismiss the email ${n.title}`}>Dismiss email</button>
              </>
              : <>
                <button className="bt pri sm xs2" onClick={() => decide.approve({ ...act, title: n.title })} disabled={busy}>Approve</button>
                <button className="bt sm xs2" onClick={() => decide.ignore({ ...act, title: n.title })} disabled={busy}>Ignore</button>
                <Link className="nsee xs" to={`/requests?id=${act.id}`}>{SEE[act.kind] ?? 'See details'}</Link>
              </>
          ) : (
            <motion.span key={act.status} className={`nst xs${act.status === 'approved' ? ' apd' : ''}`} initial={{ opacity: 0, y: 3 }} animate={{ opacity: 1, y: 0, transition: spring.gentle }}>
              {act.status === 'approved' && <DrawnCheck size={12} />}{STATUS_WORD[act.status]}
            </motion.span>
          ))}
          {canAnswer && on && !answering && <button className="bt sm xs2" onClick={() => setAnswering(true)}>Answer</button>}
          <span className="ntm xs mut" title={via.length ? `Also by ${via.join(', ')}` : 'In the app'}>{when(n.createdAt)}</span>
          {on
            ? <button type="button" className="nxb" onClick={onDismiss} aria-label="Dismiss" data-tip="Dismiss" title="Dismiss"><IconX /></button>
            : <span className="nxb done" aria-label="Dismissed" title="Dismissed"><IconCheck /></span>}
        </div>
      </div>
      <NoteDetail detail={n.detail} />
      {canAnswer && <AnswerBox checkId={n.teamCheckId!} on={on} open={answering} />}
    </div>
  );
}

/** Answer a question from its notification: Answer opens the box; Send sends it. */
function AnswerBox({ checkId, on, open }: { checkId: string; on: boolean; open: boolean }) {
  const answering = open;
  const [answer, setAnswer] = useState('');
  const [sent, setSent] = useState(false);
  const reply = useChange((t: string) => api.checks.answer(checkId, t));
  const submit = (e: FormEvent) => { e.preventDefault(); if (answer.trim()) reply.mutate(answer.trim(), { onSuccess: () => setSent(true) }); };
  return (
    <>
      <AnimatePresence initial={false}>
        {on && !sent && answering && (
          <motion.form key="a" className="askmini nans" onSubmit={submit} style={{ maxWidth: 560 }} initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0, transition: spring.gentle }} exit={{ opacity: 0 }}>
            <input value={answer} onChange={e => setAnswer(e.target.value)} placeholder="Type your answer" aria-label="Answer" autoFocus />
            <button className="bt sm" disabled={reply.isPending}>{reply.isPending ? <span className="spin" /> : null}Send</button>
          </motion.form>
        )}
      </AnimatePresence>
      {sent && <span className="apd nans"><DrawnCheck size={15} />Answer sent.</span>}
      {reply.error ? <ErrorNote error={reply.error} /> : null}
    </>
  );
}

/** "You hear about": one line per category with how it reaches you; Change turns the lines into switches. */
function HearAbout() {
  const p = usePrefs();
  const update = useUpdatePrefs();
  const [edit, setEdit] = useState(false);
  const first = useRef<HTMLButtonElement>(null);
  const ch = p.data?.notifications.channels;
  const quiet = p.data?.notifications.quietHours ?? null;
  const set = (c: NotificationCategory, k: 'push' | 'email', v: boolean) => update.mutate({ notifications: { channels: { [c]: { [k]: v } } } });
  const words = (c: NotificationCategory, x: UserPrefs['notifications']['channels'][NotificationCategory]) =>
    ['App', x.push && 'push', x.email && 'email'].filter(Boolean).join(', ') + (c === 'knowledge' && !x.push && !x.email ? '' : '');
  return (
    <motion.aside layout className="panel hear" id="hear" aria-label="You hear about" transition={spring.gentle}>
      <div className="sh"><b>You hear about</b>
        <button type="button" className="link xs" onClick={() => { setEdit(e => !e); window.setTimeout(() => first.current?.focus(), 50); }} aria-expanded={edit}>{edit ? 'Done' : 'Change'}</button>
      </div>
      {!ch ? <SkelList rows={6} variant="conv" /> : (
        <div className="hl">
          {NOTIFICATION_CATEGORIES.map((c, i) => (
            <motion.div layout="position" className="hr" key={c}>
              <span className="sm">{NOTIFICATION_CATEGORY_LABELS[c]}</span>
              <AnimatePresence mode="popLayout" initial={false}>
                {edit ? (
                  <motion.span key="e" className="chans" initial={{ opacity: 0, x: 6 }} animate={{ opacity: 1, x: 0, transition: spring.snappy }} exit={{ opacity: 0 }}>
                    <span className="chan on lock" title="Every notification is in the app until you dismiss it">App</span>
                    <button ref={i === 0 ? first : undefined} type="button" className={`chan${ch[c].push ? ' on' : ''}`} aria-pressed={ch[c].push} onClick={() => set(c, 'push', !ch[c].push)}>Push</button>
                    <button type="button" className={`chan${ch[c].email ? ' on' : ''}`} aria-pressed={ch[c].email} onClick={() => set(c, 'email', !ch[c].email)}>Email</button>
                  </motion.span>
                ) : <motion.span key="v" className="xs mut" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>{words(c, ch[c])}</motion.span>}
              </AnimatePresence>
            </motion.div>
          ))}
          <motion.div layout="position" className="hr">
            <span className="sm">Quiet hours</span>
            {edit ? (
              <span className="chans">
                {quiet ? <>
                  <input className="inp tm" type="time" value={quiet.from} aria-label="Quiet from" onChange={e => e.target.value && update.mutate({ notifications: { quietHours: { from: e.target.value, to: quiet.to } } })} />
                  <span className="xs mut">to</span>
                  <input className="inp tm" type="time" value={quiet.to} aria-label="Quiet until" onChange={e => e.target.value && update.mutate({ notifications: { quietHours: { from: quiet.from, to: e.target.value } } })} />
                  <button type="button" className="chan" onClick={() => update.mutate({ notifications: { quietHours: null } })}>Off</button>
                </> : <button type="button" className="chan" onClick={() => update.mutate({ notifications: { quietHours: { from: '20:00', to: '07:00' } } })}>Turn on</button>}
              </span>
            ) : <span className="mono xs">{quiet ? `${quiet.from} to ${quiet.to}` : 'Off'}</span>}
          </motion.div>
          {edit && <motion.p layout className="xs mut" initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ margin: '8px 0 0', lineHeight: 1.5 }}>Quiet hours hold push and email until they end; the app always shows everything. Push reaches each browser where you turned it on (Settings, Notifications); email needs Amazon SES.</motion.p>}
          {update.error ? <ErrorNote error={update.error} /> : null}
        </div>
      )}
    </motion.aside>
  );
}
